/** The staged files above the question — one chip each: thumbnail (images)
 *  or kind icon, `name · size`, upload progress, the error with a retry, and
 *  a remove button. */
import { Avatar, Box, Chip, CircularProgress, Stack, Tooltip } from '@mui/material';

import { formatBytes } from '@/utils/formatBytes';

import { guessKind, KindIcon } from './kind';
import type { StagedAttachment } from './useAttachmentStaging';

function ChipAvatar({ item }: { item: StagedAttachment }) {
  if (item.state === 'uploading') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24 }}>
        <CircularProgress
          size={18}
          variant={item.progress > 0 ? 'determinate' : 'indeterminate'}
          value={Math.round(item.progress * 100)}
          aria-label={`Uploading ${item.file.name}`}
        />
      </Box>
    );
  }
  if (item.previewUrl) {
    return <Avatar variant="rounded" src={item.previewUrl} alt="" />;
  }
  const kind = item.descriptor?.kind ?? guessKind(item.file.type, item.file.name);
  return (
    <Avatar variant="rounded" sx={{ bgcolor: 'transparent', color: 'text.secondary' }}>
      <KindIcon kind={kind} contentType={item.descriptor?.content_type ?? item.file.type} fontSize="small" />
    </Avatar>
  );
}

export function AttachmentTray({
  items,
  onRemove,
  onRetry,
  disabled = false,
}: {
  items: StagedAttachment[];
  onRemove: (localId: string) => void;
  onRetry: (localId: string) => void;
  disabled?: boolean;
}) {
  if (items.length === 0) return null;
  return (
    <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} aria-label="Attached files">
      {items.map((item) => {
        const label = `${item.file.name} · ${formatBytes(item.file.size)}`;
        const failed = item.state === 'error';
        const note = item.descriptor?.note;
        const chip = (
          <Chip
            size="medium"
            variant="outlined"
            color={failed ? 'error' : 'default'}
            avatar={<ChipAvatar item={item} />}
            label={failed ? `${label} — ${item.error ?? 'failed'}` : label}
            onClick={failed && !disabled ? () => onRetry(item.localId) : undefined}
            onDelete={disabled ? undefined : () => onRemove(item.localId)}
            sx={{ maxWidth: 360 }}
          />
        );
        const tip = failed
          ? 'Upload failed — click to retry'
          : note
            ? note
            : item.state === 'uploading'
              ? 'Uploading…'
              : '';
        return (
          <Tooltip key={item.localId} title={tip}>
            {chip}
          </Tooltip>
        );
      })}
    </Stack>
  );
}
